import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

// ⚠️ CWS-178, a deliberate behavioural edit to this vendored primitive (the
// ui.md review rule asks that such edits be declared): focus is now RESTORED
// on close. Radix restores focus to the registered `DialogTrigger` — but the
// lead modals open through a context provider with their triggers living on
// pages across the site and no `DialogTrigger` in the tree, so Escape or the
// Close button dropped a keyboard visitor onto `document.body` with their
// place lost (measured: e2e/a11y.spec.ts's keyboard journeys, and the probe
// under .requirements/20260917T081819Z_axe_contrast_gate/). The element
// focused when the dialog opens is captured and focus returns to it on
// close. A consumer's own handler still runs first and can preventDefault to
// opt out, and where a real `DialogTrigger` exists Radix's own default runs
// after this and keeps winning — the documented behavior is unchanged there.
//
// The composition claim is checkable against the installed dependency, not
// taken on trust: @radix-ui/react-dialog's DialogContentModal passes
// `onCloseAutoFocus: composeEventHandlers(props.onCloseAutoFocus, (event) => {
// event.preventDefault(); context.triggerRef.current?.focus(); })`
// (dist/index.mjs, the DialogContentModal props), and @radix-ui/primitive's
// composeEventHandlers calls the ORIGINAL (user) handler first and runs the
// internal default only when the event is not defaultPrevented. Order on
// close: consumer handler → this restore → Radix's trigger focus (a `?.`
// no-op when no trigger is registered).
const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, onOpenAutoFocus, onCloseAutoFocus, ...props }, ref) => {
  const previouslyFocused = React.useRef<HTMLElement | null>(null);
  const handleOpenAutoFocus = (event: Event) => {
    onOpenAutoFocus?.(event);
    // Captured before Radix's own autofocus moves focus into the dialog —
    // this handler composes ahead of the primitive's default.
    if (!event.defaultPrevented) {
      previouslyFocused.current = document.activeElement as HTMLElement | null;
    }
  };
  const handleCloseAutoFocus = (event: Event) => {
    onCloseAutoFocus?.(event);
    // A no-op when the captured element has since unmounted (focus() on a
    // detached node), which is the pre-fix behavior for that flow anyway.
    if (!event.defaultPrevented) {
      previouslyFocused.current?.focus();
    }
  };
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
          className,
        )}
        onOpenAutoFocus={handleOpenAutoFocus}
        onCloseAutoFocus={handleCloseAutoFocus}
        {...props}
      >
        {children}
        <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity data-[state=open]:bg-accent data-[state=open]:text-muted-foreground hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)} {...props} />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)} {...props} />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
